import React, { useLayoutEffect, useState } from 'react'
import H from '@here/maps-api-for-javascript'
import './scss/HereMap.scss'
import vwSvgMarker from './resources/svgMarker_vw.svg'
import seatSvgMarker from './resources/svgMarker_seat.svg'
import skodaSvgMarker from './resources/svgMarker_skoda.svg'
import audiSvgMarker from './resources/svgMarker_audi.svg'
import bluelabelSvgMarker from './resources/svgMarker_bluelabel.svg'

export type Brand = 'vw' | 'audi' | 'seat' | 'skoda' | 'vwfs' | 'generic'

export type HereMapMarkerType = {
  markerId: string
  regionId: string
  latitude: number
  longitude: number
  infoBubble: string // e.g. use DefaultInfoBubble
  brand: Brand
  buttonLabel: string
}
export type BubblePositionType = {
  longitude: number
  latitude: number
}
export type HereMapSVGMarker = string | HTMLImageElement | HTMLCanvasElement
export type infoBubbleSize = {
  w: number
  h: number
}
export type ViewCallback = {
  latitude: number
  longitude: number
  zoom: number
  radius: number
}
export type HereMapProps = {
  apikey: string
  className: string // TODO: css classname
  markers: HereMapMarkerType[]
  height: string
  width: string
  mapLatitude: number
  mapLongitude: number
  uiLanguage: string
  mapLanguage: string
  controlsVisibility: boolean
  onClickMarker: (markerId: string) => void
  onViewChanged?: (e: ViewCallback) => void
  zoom: number
  dynamicBubble: boolean
  singleBubble: boolean
  brandStyle?: Brand
  clustering?: boolean
  clusteringRadius?: number
  country?: string
}

export const HereMap: React.FC<HereMapProps> = ({
  apikey,
  className,
  markers,
  height = '100%',
  width = '100%',
  mapLatitude,
  mapLongitude,
  uiLanguage = 'de-DE',
  mapLanguage = 'deu',
  controlsVisibility = false,
  onClickMarker,
  onViewChanged,
  zoom = 14,
  dynamicBubble = false, // Keep bubles inside view on view change
  singleBubble = true, // Allow only one bubble open
  brandStyle = 'generic',
  clustering = false, // Group near markers
  clusteringRadius = 30,
  country
}) => {
  // TODO: Clean dead code (single bubble by default, remove related code)
  const mapRef = React.useRef(null)
  const [clusteringLayerPointer, setClusteringLayerPointer] = useState<H.map.layer.ObjectLayer | undefined>(undefined)
  const [mapPointer, setMapPointer] = useState<H.Map | undefined>(undefined)
  const [uiPointer, setUiPointer] = useState<any>(undefined)
  const omvServicePath = 'v2/vectortiles/core/mc';
  const japanMarketBaseUrl = 'https://js.api.here.com/v3/3.1/styles/omv/oslo/japan/';

  const svgMarker: any = (brand) => {
    switch (brand) {
      case 'vw':
        return vwSvgMarker
      case 'audi':
        return audiSvgMarker
      case 'seat':
        return seatSvgMarker
      case 'skoda':
        return skodaSvgMarker
      case 'vwfs':
      case 'multiBrand':
      default:
        return bluelabelSvgMarker
    }
  }
  const getInfoBubbleBody = (infoBubble): HTMLElement => {
    return (infoBubble.getElement().querySelectorAll('.H_ib_body') as HTMLElement)[0]
  }

  // Place bubbles to keep inside view
  const locateBubble = (map, bubbles): void => {
    const ARROW_X_OFFSET = 50 // Horizontal offset of arrow
    const INFOBUBBLE_Y_OFFSET = 40 // Vertical offset of infobubble
    const getInfoBubbleSize = (e): infoBubbleSize => {
      return {
        w: getInfoBubbleBody(e).offsetWidth,
        h: getInfoBubbleBody(e).offsetHeight,
      }
    }
    bubbles.forEach((bub) => {
      const xy = map.geoToScreen({ lat: bub.getData().latitude, lng: bub.getData().longitude })
      const infoBubbleFitY = xy.y < getInfoBubbleSize(bub).h + INFOBUBBLE_Y_OFFSET
      const offsetX = xy.x > getInfoBubbleSize(bub).w ? 0 : getInfoBubbleSize(bub).w - xy.x
      getInfoBubbleBody(bub).style.setProperty(
        'transform',
        `translatex(${
          offsetX < getInfoBubbleSize(bub).w - ARROW_X_OFFSET ? offsetX : getInfoBubbleSize(bub).w - ARROW_X_OFFSET
        }px)`,
        'important',
      )

      if (infoBubbleFitY) {
        bub.getElement().classList.add('infobubble_reverse_y')
      } else {
        bub.getElement().classList.remove('infobubble_reverse_y')
      }
    })
  }
  const onClickMarkerOwn = (e): void => {
    // Callback to send marker clicked info to host
    onClickMarker(e)
  }

  // Inject data to map
  const startClustering = (map, data): null => {
    if (!mapPointer) {
      return null
    }

    // Make datapoints from markers provided
    const dataPoints = data.map((item) => {
      return new H.clustering.DataPoint(item.latitude, item.longitude, undefined, item)
    })
    // Create clustered data from datapoints
    const clusteredDataProvider = new H.clustering.Provider(dataPoints, {
      theme: {
        getClusterPresentation(cluster) {
          const weight = cluster.getWeight()
          // Get bigger size when more dealers inside
          // const radius = weight / 4 > 2 ? 30 : (weight / 4) * 10 + 10
          const iconRadius = 36
          const diameter = iconRadius * 2

          const svgString = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 50 50" width="40px" height="40px">
                <g opacity="0.3">
                <path d="M25,3A22,22,0,1,1,3,25,22,22,0,0,1,25,3m0-3A25,25,0,1,0,50,25,25.028,25.028,0,0,0,25,0Z" fill="#afb5ab"/>
                </g>
                <g opacity="0.6">
                  <path d="M25,7A18,18,0,1,1,7,25,18,18,0,0,1,25,7m0-3A21,21,0,1,0,46,25,21.024,21.024,0,0,0,25,4Z" fill="#afb5ab"/>
                </g>
                <path d="M25,11A14,14,0,1,1,11,25,14,14,0,0,1,25,11m0-3A17,17,0,1,0,42,25,17.019,17.019,0,0,0,25,8Z" fill="#afb5ab"/>
                <circle cx="25" cy="25" r="12.5" fill="#3e4547"/>
                <text font-family="'Helvetica Neue',Helvetica,Arial,sans-serif" font-size="12" font-weight="500" fill="#ECECEC" x="50%" y="50%" text-anchor="middle" alignment-baseline="middle" dominant-baseline="middle">${weight.toString()}</text>
                </svg>`
          const clusterIcon = new H.map.Icon(svgString, {
            size: { w: diameter, h: diameter },
            anchor: { x: iconRadius, y: iconRadius },
          })

          const clusterMarker = new H.map.Marker(cluster.getPosition(), {
            icon: clusterIcon,
            // Set min/max zoom with values from the cluster, otherwise
            // clusters will be shown at all zoom levels
            min: Number(cluster.getMinZoom()),
            max: Number(cluster.getMaxZoom()),
            data: {},
          })
          clusterMarker.setData(cluster)
          return clusterMarker
        },
        getNoisePresentation(noisePoint: any) {
          const icon = new H.map.Icon(svgMarker(noisePoint.getData().brand))
          const noiseMarker = new H.map.Marker(noisePoint.getPosition(), {
            icon,
            data: {},
            // Use min zoom from a noise point to show it correctly at certain zoom levels
            min: Number(noisePoint.getMinZoom()),
          })
          noiseMarker.setData({
            regionId: noisePoint.getData().regionId,
            markerId: noisePoint.getData().markerId,
            ui: noisePoint.getData().infoBubble,
            position: { latitude: noisePoint.getData().latitude, longitude: noisePoint.getData().longitude },
            brand: noisePoint.getData().brand,
          })

          return noiseMarker
        },
      },
      clusteringOptions: {
        // Maximum radius of the neighbourhood
        eps: clusteringRadius,
        // minimum weight of points required to form a cluster
        minWeight: clustering ? 2 : undefined,
      },
    })
    // Create Clustered layer from clustered data
    const clusteringLayer = new H.map.layer.ObjectLayer(clusteredDataProvider)
    // Add layer to state to allow delete
    setClusteringLayerPointer(clusteringLayer)
    // Add layer map
    map.addLayer(clusteringLayer)

    clusteredDataProvider.addEventListener('tap', (evt) => {
      // Skip callback when click a combined marker with many dealres inside
      if (evt.target.getData().markerId !== 'mixed') {
        onClickMarkerOwn({ companyId: evt.target.getData().markerId, regionId: evt.target.getData().regionId })
      }
    })
    return null
  }

  // Merge bubbles of dealers with shared location inside a single marker
  const mergeMarkersLocation = (markersList): HereMapMarkerType[] => {
    const mergedMarkers = []
    markersList.map((e, j) => {
      const sameLocationIndex = mergedMarkers.findIndex(
        (marker) => marker.latitude === e.latitude && marker.longitude === e.longitude,
      )
      if (sameLocationIndex === -1) {
        e.infoBubble = `<div id="${e.markerId}" data-regionid="${
          e.regionId
        }" class="dealer_content" ><img class="brand_icon" src="${svgMarker(e.brand)}">${e.infoBubble}</div>`
        mergedMarkers.push(e)
      } else {
        mergedMarkers[sameLocationIndex].infoBubble += `<div class="dealer_content" data-regionid="${
          e.regionId
        }"  id="${e.markerId}" ><img class="brand_icon" src="${svgMarker(e.brand)}">${e.infoBubble}</div>`
        mergedMarkers[sameLocationIndex].markerId = 'mixed'
        if (mergedMarkers[sameLocationIndex].brand !== markersList[j].brand)
          mergedMarkers[sameLocationIndex].brand = 'multiBrand'
      }
      return null
    })
    return mergedMarkers
  }

  // Starts when HTML element is avaliable: Initialize Here Maps
  useLayoutEffect(() => {
    if (!mapRef.current) return undefined
    // Initialize here maps
    const platform = new H.service.Platform({ apikey })
    const defaultLayers = platform.createDefaultLayers({
      lg: mapLanguage,
    })


    var omvService = platform.getOMVService({ path: omvServicePath })
    var style = new H.map.Style(`${japanMarketBaseUrl}normal.day.yaml`, japanMarketBaseUrl)

    // instantiate provider and layer for the basemap
    var omvProvider = new H.service.omv.Provider(omvService, style)
    var omvlayer = new H.map.layer.TileLayer(omvProvider, { max: 22 })

    const hmap = new H.Map(mapRef.current, country ? omvlayer : defaultLayers.vector.normal.map, {
        center: { lat: mapLatitude, lng: mapLongitude },
        zoom,
        pixelRatio: window.devicePixelRatio || 1,
      })

    // Add hmap to state
    setMapPointer(hmap)
    // remark: this need to be called otherwise "tap" event will not trigger (bad design of heremaps as here are side effects)
    const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(hmap))

    const ui = H.ui.UI.createDefault(hmap, defaultLayers, uiLanguage)
    setUiPointer(ui)
    ui.getControl('mapsettings').setVisibility(controlsVisibility)

    const viewChangeHandler = (): void => {
      if (onViewChanged) {
        const { bounds } = hmap.getViewModel().getLookAtData()
        const points = bounds.getBoundingBox()
        const topLeft = new H.geo.Point(points.getLeft(), points.getTop())
        const bottomRight = new H.geo.Point(points.getRight(), points.getBottom())
        // Radius of view in km
        const dist = Math.round(topLeft.distance(bottomRight) / 2) / 1000
        onViewChanged({
          latitude: hmap.getCenter().lat,
          longitude: hmap.getCenter().lng,
          zoom: hmap.getZoom(),
          radius: dist,
        })
      }
      if (dynamicBubble) {
        locateBubble(hmap, ui.getBubbles())
      }
    }
    hmap.addEventListener('mapviewchangeend', viewChangeHandler)
    const resizeHandler = (): void => hmap.getViewPort().resize()
    window.addEventListener('resize', resizeHandler)
    return () => {
      window.removeEventListener('resize', resizeHandler)
      hmap.dispose()
    }
  }, [mapRef])

  // Starts when map is avaliable or markers are changed: remove old layer (and bubbles)and add the new one
  useLayoutEffect(() => {
    if (uiPointer) uiPointer.getBubbles().forEach((bub) => uiPointer.removeBubble(bub))
    if (mapPointer) {
      mapPointer.removeLayer(clusteringLayerPointer)
      startClustering(mapPointer, mergeMarkersLocation(markers))
    }
  }, [mapPointer, markers])

  // add click event to markers when refreshed
  useLayoutEffect(() => {
    if (uiPointer) {
      uiPointer.getBubbles().forEach((bub) => uiPointer.removeBubble(bub))
    }
    const tapHandler = (evt): void => {
      // If clicked on marker(has target.data): create bubble, if clicked on cluster, center on cluster and increase zoom
      if (evt.target.data) {
        if (evt.target.getData().markerId || evt.target.markerId) {
          const bubble = new H.ui.InfoBubble(evt.target.getGeometry(), {
            content: `<div class="${evt.target.getData().brand !== 'multiBrand' ? 'single_brand' : 'multi_brand'}  ${
              evt.target.getData().markerId !== 'mixed' ? 'single_dealer' : ''
            }">${evt.target.getData().ui}</div>`,
          })

          let skipOpenInfo = false
          uiPointer.getBubbles().forEach((bub) => {
            if (evt.target.getData().markerId === bub.getData().infoBubbleId && bub.getState() === 'open') {
              uiPointer.removeBubble(bub)
              skipOpenInfo = true
            } else if (singleBubble === true) {
              uiPointer.removeBubble(bub)
            }
          })

          if (!skipOpenInfo) {
            uiPointer.addBubble(bubble)
            bubble.getElement().addEventListener('click', function (e) {
              const target = e.target as HTMLButtonElement
              const rootTarget = target.parentNode.parentNode as HTMLButtonElement
              if (target.type === 'button')
                onClickMarkerOwn({ companyId: rootTarget.id, regionId: rootTarget.getAttribute('data-regionid') })
            })

            const transitionDuration = window.getComputedStyle(getInfoBubbleBody(bubble)).transitionDuration
            bubble.setData({
              infoBubbleId: evt.target.getData().markerId,
              latitude: evt.target.getGeometry().lat,
              longitude: evt.target.getGeometry().lng,
            })
            // Avoid animate position on first appearance
            setTimeout(() => {
              getInfoBubbleBody(bubble).style.setProperty('transition-property', `all`, 'important')
            }, Number(transitionDuration))
            locateBubble(mapPointer, [bubble])
          }
        } else {
          mapPointer.setCenter({ lat: evt.target.getGeometry().lat, lng: evt.target.getGeometry().lng }, true)
          setTimeout(() => {
            mapPointer.setZoom(mapPointer.getZoom() + 1.2, true)
          }, 300)
        }
      }
    }
    if (mapPointer) {
      mapPointer.addEventListener('tap', tapHandler)
    }
  }, [markers])

  useLayoutEffect(() => {
    if (mapPointer) mapPointer.setCenter({ lat: mapLatitude, lng: mapLongitude }, true)
  }, [mapLatitude, mapLongitude])

  useLayoutEffect(() => {
    if (mapPointer) mapPointer.setZoom(zoom, true)
  }, [zoom])

  return (
    <div
      className={`${className} ${brandStyle === 'vwfs' ? 'bluelabel' : brandStyle}`}
      ref={mapRef}
      style={{ height, width }}
    />
  )
}
